// kitchen/web/src/hooks/useClientConfig.ts
// Fetches the client KDS configuration (kdsBoardConfig) from the backend.
// Provides a default board fallback when data is not yet available. The
// server's board (in the installation's language when the venue saved none)
// wins whenever it is present; the fallback's group names follow this screen's
// language (feature 298).

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { clientConfigApi, type ClientConfig } from '../api/client';
import { defaultKdsBoardConfig, type KdsBoardConfig } from '@shared/kds/table-board';
import { useTranslation } from '../i18n';

export function useClientConfig(clientId: string | null): {
  config: KdsBoardConfig;
  clientConfig: ClientConfig | undefined;
  isLoading: boolean;
} {
  const { t } = useTranslation();
  const fallback = useMemo(() => defaultKdsBoardConfig(t), [t]);
  const { data, isLoading } = useQuery<ClientConfig>({
    queryKey: ['kitchen-client-config', clientId],
    queryFn: () => clientConfigApi.get(clientId!),
    enabled: !!clientId,
    // Feature 252 — the venue switch rides this query, and flipping it must
    // reach the kitchen display promptly (SC-005). A minute of stale time meant
    // a cook could watch a colleague's screen regroup while his own had not.
    //
    // Measured end to end on 11/09/2026: 5.3 s on, 6.7 s off. Three seconds of
    // that is this poll; the rest is the backend's own 4-second cache on the
    // configuration endpoint. Lowering this further would not help much and
    // would cost a request every second or two per screen.
    //
    // Safe to poll: this payload is a handful of flags plus the board config,
    // on the order of a kilobyte. The refetch storm that once wedged the
    // kitchen display was the ORDERS query at ~576 KB a call — do not copy
    // this interval onto that one.
    staleTime: 3_000,
    refetchInterval: 3_000,
  });

  const config: KdsBoardConfig = data?.kdsBoardConfig ?? fallback;

  return { config, clientConfig: data, isLoading };
}
